import React, { useEffect, useRef, useState } from 'react';
import { App } from '@capacitor/app';
import { checkForUpdate, openStore } from '../utils/appUpdate';
import './AppUpdatePrompt.css';

const SNOOZE_KEY = 'emsUpdatePromptSnoozedUntil';
const SNOOZE_MS = 24 * 60 * 60 * 1000; // remind again after 24h for optional updates

/**
 * Shows a branded "Update available" popup when a newer build exists on the
 * App Store / Play Store. Optional updates are dismissible (snoozed 24h);
 * updates below `minimumVersion` are blocking ("Update required").
 */
export default function AppUpdatePrompt() {
  const [update, setUpdate] = useState(null);
  const [busy, setBusy] = useState(false);
  const checkedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const info = await checkForUpdate();
        if (cancelled || !info) return;

        if (!info.forced) {
          const snoozedUntil = Number(localStorage.getItem(SNOOZE_KEY) || 0);
          if (Date.now() < snoozedUntil) return;
        }
        setUpdate(info);
      } catch (e) {
        /* ignore */
      }
    };

    // Initial check (slight delay so it doesn't fight the splash/login).
    const t = setTimeout(run, 1500);
    checkedRef.current = true;

    // Re-check when the app returns to the foreground.
    let sub;
    (async () => {
      try {
        sub = await App.addListener('appStateChange', ({ isActive }) => {
          if (isActive) run();
        });
      } catch (e) {
        /* ignore */
      }
    })();

    return () => {
      cancelled = true;
      clearTimeout(t);
      if (sub && sub.remove) sub.remove();
    };
  }, []);

  if (!update) return null;

  const handleUpdate = () => {
    setBusy(true);
    openStore(update.storeUrl);
    // Keep the (non-forced) sheet from instantly reappearing after returning.
    if (!update.forced) {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
      setTimeout(() => setUpdate(null), 400);
    } else {
      setTimeout(() => setBusy(false), 1200);
    }
  };

  const handleLater = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    setUpdate(null);
  };

  return (
    <div
      className="ems-update"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ems-update-title"
      onClick={(e) => {
        if (!update.forced && e.target === e.currentTarget) handleLater();
      }}
    >
      <div className="ems-update__card">
        <div className="ems-update__icon" aria-hidden="true">
          <svg viewBox="0 0 512 512" width="64" height="64" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="upb" x1="60" y1="36" x2="452" y2="476" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#B79CFB" /><stop offset=".45" stopColor="#8B5CF6" /><stop offset="1" stopColor="#6429C9" />
              </linearGradient>
            </defs>
            <path d="M256 24C150 24 96 24 60 60S24 150 24 256s0 106 36 142 90 36 196 36 106 0 142-36 36-90 36-196 0-106-36-142S362 24 256 24Z" fill="url(#upb)" />
            <path d="M256 150 L256 330 M256 150 L196 214 M256 150 L316 214" fill="none" stroke="#fff" strokeWidth="34" strokeLinecap="round" strokeLinejoin="round" />
            <rect x="176" y="360" width="160" height="34" rx="17" fill="#fff" />
          </svg>
        </div>

        <h2 id="ems-update-title" className="ems-update__title">
          {update.forced ? 'Update required' : 'Update available'}
        </h2>
        <p className="ems-update__msg">{update.message}</p>

        <div className="ems-update__versions">
          <span className="ems-update__v ems-update__v--old">v{update.currentVersion}</span>
          <span className="ems-update__arrow" aria-hidden="true">→</span>
          <span className="ems-update__v ems-update__v--new">v{update.latestVersion}</span>
        </div>

        <button className="ems-update__btn" onClick={handleUpdate} disabled={busy}>
          {busy ? 'Opening store…' : 'Update now'}
        </button>
        {!update.forced && (
          <button className="ems-update__later" onClick={handleLater}>
            Maybe later
          </button>
        )}
      </div>
    </div>
  );
}
